import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { Home, Video, Radio, LogIn, ShieldCheck, Trophy, CircleDot, Clock, MapPin, X, Zap } from 'lucide-react';
import { auth } from '../../api/firebase';
import { getArena, getVideoHours, getVideosByHour } from '../../api';
import { MenuBar } from '../../components/ui/glow-menu';
import VideosList from '../Videos/components/VideosList';
import { getRecentCourts, addRecentCourt, removeRecentCourt, FASTTENNIS_RECENT_KEY } from '../../utils/recentCourts';
import type { RecentCourt } from '../../utils/recentCourts';
import logoImg from '../../assets/images/logo.svg';
import styles from './index.module.css';

type Option = { id: string; value: string };

type Arena = {
  id: string;
  name: string;
  city: string;
  subCourts: any[];
};

const optionsState: Option[] = [
  { id: 'mg', value: 'MG' },
  { id: 'ba', value: 'BA' },
];

const citiesByState: Record<string, Option[]> = {
  mg: [
    { id: 'belohorizonte', value: 'Belo Horizonte' },
    { id: 'diamantina', value: 'Diamantina' },
    { id: 'setelagoas', value: 'Sete Lagoas' },
    { id: 'perdoes', value: 'Perdões' },
    { id: 'itamarandiba', value: 'Itamarandiba' },
  ],
  ba: [{ id: 'Jequie', value: 'Jequié' }],
};

// Nome no bucket/Firestore → rótulo exibido no site
const ARENA_LABELS: Record<string, string> = {
  'Fast Tennis': 'Fast Pampulha',
};
const arenaLabel = (name?: string | null) => (name ? ARENA_LABELS[name] ?? name : '');

const menuItems = [
  {
    icon: Home, label: 'Início', href: '/',
    gradient: 'radial-gradient(circle, rgba(245,138,37,0.2) 0%, rgba(229,118,5,0.08) 50%, transparent 100%)',
    iconColor: 'text-orange-500',
  },
  {
    icon: Video, label: 'Vídeos', href: '/videos',
    gradient: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, transparent 100%)',
    iconColor: 'text-blue-500',
  },
  {
    icon: CircleDot, label: 'FastTennis', href: '/fasttennis',
    gradient: 'radial-gradient(circle, rgba(43,76,255,0.25) 0%, rgba(43,76,255,0.08) 50%, transparent 100%)',
    iconColor: 'text-blue-400',
  },
  {
    icon: Radio, label: 'Ao Vivo', href: '/livestream',
    gradient: 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.06) 50%, transparent 100%)',
    iconColor: 'text-red-500',
  },
  {
    icon: ShieldCheck, label: 'FutSafe', href: '/futsafe',
    gradient: 'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 100%)',
    iconColor: 'text-yellow-500',
  },
  {
    icon: Trophy, label: 'Torneios', href: '/torneios',
    gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
  {
    icon: LogIn, label: 'Sair', href: '/login',
    gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
];

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function formatDateBR(iso: string) {
  if (!iso) return '';
  const [y, mo, da] = iso.split('-');
  return `${da}/${mo}/${y}`;
}

// 'YYYY-MM-DD' -> '19 jul'
function formatDateLabel(date: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!match) return undefined;
  const day = parseInt(match[3], 10);
  const month = MONTHS_PT[parseInt(match[2], 10) - 1];
  return month ? `${day} ${month}` : undefined;
}

function FastTennis() {
  const navigate = useNavigate();

  const [state, setState] = useState<Option>(optionsState[0]);
  const [city, setCity] = useState<Option>(citiesByState['mg'][0]);
  const [arena, setArena] = useState<Arena | null>(null);
  const [court, setCourt] = useState<Option | null>(null);
  const [date, setDate] = useState<string>(todayISO());
  const [selectedHour, setSelectedHour] = useState<number | null>(null);

  const [shouldFetchHours, setShouldFetchHours] = useState(false);
  const [shouldFetchVideos, setShouldFetchVideos] = useState(false);
  const [recentCourts, setRecentCourts] = useState<RecentCourt[]>([]);

  // evita que os efeitos de reset limpem seleções feitas em bloco (atalhos)
  const skipResetRef = useRef(false);

  useEffect(() => {
    setRecentCourts(getRecentCourts(FASTTENNIS_RECENT_KEY));
  }, []);

  // arenas by city
  const { data: dataArena } = useQuery({
    queryKey: ['getArena', city?.value ?? ''],
    queryFn: getArena,
    enabled: !!city,
  });

  // filter FastTennis arenas only (name or id contains 'fast')
  const ftArenas: Arena[] = useMemo(() => {
    if (!Array.isArray(dataArena)) return [];
    return dataArena.filter((a: Arena) => {
      const haystack = `${a.name ?? ''} ${a.id ?? ''}`.toLowerCase();
      return haystack.includes('fast');
    });
  }, [dataArena]);

  // reset dependent fields when upstream changes
  useEffect(() => {
    setCity((prev) =>
      prev && (citiesByState[state.id] ?? []).some((c) => c.id === prev.id)
        ? prev
        : (citiesByState[state.id]?.[0] ?? (null as any))
    );
  }, [state]);
  useEffect(() => {
    if (skipResetRef.current) return;
    setArena(null); setCourt(null); setSelectedHour(null);
  }, [city]);
  useEffect(() => {
    if (skipResetRef.current) return;
    setCourt(null); setSelectedHour(null);
  }, [arena]);
  useEffect(() => { skipResetRef.current = false; });

  // só temos uma unidade por cidade: deixa sempre selecionada
  useEffect(() => {
    if (ftArenas.length === 1) {
      setArena((prev) => (prev && prev.id === ftArenas[0].id ? prev : ftArenas[0]));
    }
  }, [ftArenas]);

  // hours
  const { data: dataHours, refetch: refetchHours, isFetching: isFetchingHours } = useQuery({
    queryKey: [
      'getVideoHours',
      state?.id ?? '',
      city?.id ?? '',
      arena?.name ?? '',
      date,
      court?.value ?? '',
    ],
    queryFn: getVideoHours,
    enabled: shouldFetchHours && !!date && !!arena && !!court,
  });

  // videos for selected hour
  const { data: dataVideos, isFetching: isFetchingVideos } = useQuery({
    queryKey: [
      'getVideosByHour',
      state?.id ?? '',
      city?.id ?? '',
      arena?.name ?? '',
      date,
      court?.value ?? '',
      selectedHour,
    ],
    queryFn: getVideosByHour,
    enabled: shouldFetchVideos && !!date && !!arena && !!court && selectedHour !== null,
  });

  const saveRecent = (a: Arena, c: Option) => {
    addRecentCourt(
      {
        state: { id: state.id, value: state.value },
        city: { id: city.id, value: city.value },
        arena: { id: a.id, value: a.name, subCourts: a.subCourts },
        court: { id: c.id, value: c.value },
      },
      FASTTENNIS_RECENT_KEY
    );
    setRecentCourts(getRecentCourts(FASTTENNIS_RECENT_KEY));
  };

  const handleSearch = () => {
    if (!arena || !court || !date) return;
    saveRecent(arena, court);
    setSelectedHour(null);
    setShouldFetchHours(true);
    refetchHours();
  };

  // atalho "Vídeos de hoje": preenche tudo e já busca os horários de hoje
  const handleToday = () => {
    const a = arena ?? ftArenas[0] ?? null;
    if (!a) return;
    const opts: Option[] = (a.subCourts ?? []).map((s: any) =>
      typeof s === 'string' ? { id: s, value: s } : { id: s.id ?? s.value ?? s.name, value: s.value ?? s.name ?? s.id }
    );
    const c = court ?? opts[0] ?? null;
    if (!c) return;
    skipResetRef.current = true;
    setArena(a);
    setCourt(c);
    setDate(todayISO());
    saveRecent(a, c);
    setSelectedHour(null);
    setShouldFetchHours(true);
    refetchHours();
  };

  // atalho de "Últimas quadras": restaura a busca salva e já busca os horários
  const applyRecent = (r: RecentCourt) => {
    const st = optionsState.find((o) => o.id === r.state.id) ?? optionsState[0];
    const ct = (citiesByState[st.id] ?? []).find((c) => c.id === r.city.id) ?? citiesByState[st.id]?.[0];
    const a: Arena = { id: r.arena.id, name: r.arena.value, city: ct?.value ?? '', subCourts: r.arena.subCourts ?? [] };
    skipResetRef.current = true;
    setState(st);
    if (ct) setCity(ct);
    setArena(a);
    setCourt({ id: r.court.id, value: r.court.value });
    setDate(todayISO());
    setSelectedHour(null);
    setShouldFetchHours(true);
  };

  const handleRemoveRecent = (r: RecentCourt) => {
    removeRecentCourt(r, FASTTENNIS_RECENT_KEY);
    setRecentCourts(getRecentCourts(FASTTENNIS_RECENT_KEY));
  };

  const handleClear = () => {
    setState(optionsState[0]);
    setCity(citiesByState['mg'][0]);
    setArena(null);
    setCourt(null);
    setDate(todayISO());
    setSelectedHour(null);
    setShouldFetchHours(false);
    setShouldFetchVideos(false);
  };

  const handleSelectHour = (hour: number) => {
    setSelectedHour(hour);
    setShouldFetchVideos(true);
  };

  useEffect(() => {
    if (shouldFetchHours && dataHours) setShouldFetchHours(false);
  }, [dataHours, shouldFetchHours]);

  const handleMenuClick = (label: string) => {
    if (label === 'Sair') {
      signOut(auth).finally(() => {
        localStorage.removeItem('accessToken');
        navigate('/login');
      });
      return;
    }
    const item = menuItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  const hasArenas = ftArenas.length > 0;
  const courtOptions: Option[] = useMemo(() => {
    if (!arena || !arena.subCourts) return [];
    return arena.subCourts.map((s: any) =>
      typeof s === 'string' ? { id: s, value: s } : { id: s.id ?? s.value ?? s.name, value: s.value ?? s.name ?? s.id }
    );
  }, [arena]);

  const hoursList: { hour: number; label: string }[] = Array.isArray(dataHours) ? dataHours : [];

  return (
    <div className={styles.root}>
      {/* nav */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5"
        style={{ backgroundColor: 'rgba(5,5,5,0.8)' }}
      >
        <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
        <MenuBar items={menuItems} activeItem="FastTennis" onItemClick={handleMenuClick} />
      </header>

      {/* hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div>
            <h1 className={styles.title}>
              FastTennis<br />
              <span className={styles.blue}>Pampulha</span>
            </h1>
            <p className={styles.tagline}>
              Cada aula sua, <b>gravada em alta qualidade</b> pelas câmeras Apertai. Escolha a quadra e o horário: reveja, compartilhe e evolua.
            </p>
          </div>

          <div className={styles.heroRight}>
            <div className={styles.stat}>
              <div className="v" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, color: '#6f8dff' }}>
                {ftArenas.length}
              </div>
              <div className="l" style={{ marginTop: 10, color: '#9a9a9a', fontSize: 13 }}>
                unidades FastTennis<br />em {city?.value ?? '—'}
              </div>
            </div>
            <div className={`${styles.stat} ${styles.accent}`}>
              <div className="v" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, color: '#b3c5ff' }}>
                {hoursList.length}
              </div>
              <div className="l" style={{ marginTop: 10, color: '#9a9a9a', fontSize: 13 }}>
                horários gravados<br />nesta busca
              </div>
            </div>
            <div className={`${styles.stat} ${styles.accent}`}>
              <div className="v" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, color: '#b3c5ff' }}>
                {Array.isArray(dataVideos) ? dataVideos.length : 0}
              </div>
              <div className="l" style={{ marginTop: 10, color: '#9a9a9a', fontSize: 13 }}>
                replays disponíveis<br />no horário selecionado
              </div>
            </div>
            <div className={styles.stat}>
              <div className="v" style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, color: '#6f8dff' }}>
                HD
              </div>
              <div className="l" style={{ marginTop: 10, color: '#9a9a9a', fontSize: 13 }}>
                qualidade nativa<br />· captura por câmera Apertai
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* strip */}
      <div className={styles.strip}>
        <div className={styles.stripInner}>
          <div className={styles.stripLeft}>
            <span className={`${styles.pill} ${styles.live}`}><span className="dot" /> {hasArenas ? `${ftArenas.length} unidade(s) FastTennis disponíveis` : 'Nenhuma unidade FastTennis nesta cidade'}</span>
            {arena && (
              <span className={`${styles.pill} ${styles.green}`}><span className="k">UNIDADE</span> {arenaLabel(arena.name)}</span>
            )}
            {court && (
              <span className={styles.pill}><span className="k">QUADRA</span> {court.value}</span>
            )}
          </div>
        </div>
      </div>

      {/* main */}
      <div className={styles.main}>
        {/* filters */}
        <aside className={styles.side}>
          <h3><span className={styles.underline} /> Encontre seu replay</h3>
          <p>Sua unidade já vem selecionada. Escolha a quadra e clique para ver os horários da sua aula.</p>

          <button className={styles.btnToday} onClick={handleToday} disabled={!hasArenas}>
            <Zap size={16} strokeWidth={2.4} />
            Vídeos de hoje
          </button>

          {recentCourts.length > 0 && (
            <div className={styles.recent}>
              <div className={styles.recentHead}>
                <Clock size={13} />
                <span>Últimas quadras</span>
              </div>
              <div className={styles.recentList}>
                {recentCourts.map((r, i) => (
                  <button key={i} type="button" className={styles.recentChip} onClick={() => applyRecent(r)}>
                    <MapPin size={14} />
                    <span className={styles.recentArena}>{arenaLabel(r.arena.value)}</span>
                    <span className={styles.recentCourt}>· {r.court.value}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Remover"
                      className={styles.recentRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveRecent(r);
                      }}
                    >
                      <X size={13} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.field}>
            <label>Estado</label>
            <select
              value={state.id}
              onChange={(e) => {
                const next = optionsState.find((o) => o.id === e.target.value);
                if (next) setState(next);
              }}
            >
              {optionsState.map((s) => <option key={s.id} value={s.id}>{s.value}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label>Cidade</label>
            <select
              value={city?.id ?? ''}
              onChange={(e) => {
                const cities = citiesByState[state.id] ?? [];
                const next = cities.find((c) => c.id === e.target.value);
                if (next) setCity(next);
              }}
            >
              {(citiesByState[state.id] ?? []).map((c) => <option key={c.id} value={c.id}>{c.value}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label>Unidade FastTennis</label>
            <select
              className={arena ? styles.selectGreen : ''}
              value={arena?.id ?? ''}
              onChange={(e) => {
                const next = ftArenas.find((a) => a.id === e.target.value) ?? null;
                setArena(next);
              }}
              disabled={!hasArenas}
            >
              <option value="">{hasArenas ? 'Selecione uma unidade' : 'Nenhuma unidade FastTennis nesta cidade'}</option>
              {ftArenas.map((a) => <option key={a.id} value={a.id}>{arenaLabel(a.name)}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label>Quadra</label>
            <select
              value={court?.id ?? ''}
              onChange={(e) => {
                const next = courtOptions.find((c) => c.id === e.target.value) ?? null;
                setCourt(next);
              }}
              disabled={!arena}
            >
              <option value="">{arena ? 'Selecione uma quadra' : 'Escolha a unidade primeiro'}</option>
              {courtOptions.map((c) => <option key={c.id} value={c.id}>{c.value}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label>Data</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className={styles.sideActions}>
            <button
              className={styles.btnSearch}
              onClick={handleSearch}
              disabled={!arena || !court || !date}
            >
              <svg className={styles.ico} viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
              Buscar
            </button>
            <button className={styles.btnClear} onClick={handleClear} title="Limpar filtros">
              <svg className={styles.ico} viewBox="0 0 24 24"><path d="M21 12a9 9 0 11-9-9" /><path d="M21 4v5h-5" /></svg>
              Limpar
            </button>
          </div>
        </aside>

        {/* content */}
        <div className={styles.content}>
          {arena && court && (
            <div className={styles.bc}>
              <span className={styles.bcChip}>
                <svg viewBox="0 0 24 24" style={{ width: 14, height: 14 }} className={styles.ico}><path d="M12 22s-7-7-7-12a7 7 0 0114 0c0 5-7 12-7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>
                <b>{arenaLabel(arena.name)}</b>
                <span className={styles.bcSep}>·</span>
                <span>{court.value}</span>
                <span className={styles.bcSep}>·</span>
                <span>{formatDateBR(date)}</span>
              </span>
            </div>
          )}

          {/* step 01 */}
          <div className={styles.step}>
            <div className={styles.stepHead}>
              <span className={styles.stepNum}>01</span>
              <h2>Selecione o horário</h2>
              <span className={styles.stepMeta}>
                {isFetchingHours
                  ? 'Buscando horários...'
                  : hoursList.length > 0
                    ? `${hoursList.length} horários gravados nesta quadra`
                    : shouldFetchHours
                      ? 'Nenhum horário encontrado nesta data'
                      : 'Clique em Buscar para ver os horários'}
              </span>
            </div>
            <div className={styles.timeGrid}>
              {hoursList.map((h) => (
                <button
                  key={h.hour}
                  className={`${styles.timeCard} ${selectedHour === h.hour ? styles.active : ''}`}
                  onClick={() => handleSelectHour(h.hour)}
                >
                  <div className={styles.timeCardRow}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                    {h.label}
                  </div>
                  <div className={styles.timeCardMeta}>replay · disponível</div>
                  <span className={`${styles.timeCardTag} ${styles.replay}`}>● disponível</span>
                  <span className={styles.timeCardCorner} />
                </button>
              ))}
            </div>
          </div>

          {/* step 02 */}
          <div className={styles.step}>
            <div className={styles.stepHead}>
              <span className={styles.stepNum}>02</span>
              <h2>Replays do horário</h2>
              <span className={styles.stepMeta}>
                {selectedHour === null
                  ? 'Selecione um horário acima'
                  : isFetchingVideos
                    ? 'Carregando replays...'
                    : Array.isArray(dataVideos)
                      ? `${dataVideos.filter((v: any) => v?.videoUrl).length} replays`
                      : ''}
              </span>
            </div>

            {selectedHour === null && (
              <div className={styles.empty}>
                <div>
                  <svg viewBox="0 0 64 64" width="48" height="48">
                    <circle cx="32" cy="32" r="28" fill="#d2ff7e" stroke="#08220c" strokeWidth="2" />
                    <path d="M6 28c14-8 38-8 52 0M6 36c14 8 38 8 52 0" fill="none" stroke="#08220c" strokeWidth="1.5" opacity=".5" />
                  </svg>
                </div>
                <div>
                  <h4>Escolha um horário acima</h4>
                  <p>Os replays gravados nesse horário aparecem aqui — em alta qualidade, prontos para você revisar e compartilhar.</p>
                </div>
              </div>
            )}

            {selectedHour !== null && Array.isArray(dataVideos) && dataVideos.filter((v: any) => v?.videoUrl).length === 0 && !isFetchingVideos && (
              <div className={styles.empty}>
                <div>
                  <svg viewBox="0 0 64 64" width="48" height="48">
                    <circle cx="32" cy="32" r="28" fill="#d2ff7e" stroke="#08220c" strokeWidth="2" />
                  </svg>
                </div>
                <div>
                  <h4>Nenhum replay nesse horário</h4>
                  <p>Tente outro horário ou outra data.</p>
                </div>
              </div>
            )}

            {selectedHour !== null && !isFetchingVideos && Array.isArray(dataVideos) && dataVideos.filter((v: any) => v?.videoUrl).length > 0 && (
              <VideosList
                dataVideos={dataVideos}
                variant="ft"
                courtLabel={[arenaLabel(arena?.name), court?.value].filter(Boolean).join(' · ') || undefined}
                dateLabel={formatDateLabel(date)}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.foot}>
        <span>Apertai</span> &nbsp;×&nbsp; <span>FastTennis</span> &nbsp;·&nbsp; gravado em HD · #aperteijogo
      </div>
    </div>
  );
}

export default FastTennis;
